import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback } from "react";
import { useTranslations } from "../../i18n";
import {
  addMonths,
  firstOfMonth,
  formatIsoDate,
  lastOfMonth,
  monthOf,
} from "../../utils/date";
import { Button } from "../Button/Button";
import { IconButton } from "../Button/IconButton";
import { useCalendarContext } from "./context";
import { CalendarView } from "./types";

export const Header = () => {
  const { first, last, locale, cursor, view, moveTo, showView } =
    useCalendarContext();
  const { t } = useTranslations();
  const shown = monthOf(cursor);

  const toggleMonths = useCallback(() => {
    showView(
      view === CalendarView.Months ? CalendarView.Days : CalendarView.Months,
      true,
    );
  }, [view, showView]);

  const toggleYears = useCallback(() => {
    showView(
      view === CalendarView.Years ? CalendarView.Days : CalendarView.Years,
      true,
    );
  }, [view, showView]);

  // Focus stays on the arrow, so it can be pressed again.
  const previousMonth = useCallback(
    () => moveTo(addMonths(cursor, -1), false),
    [cursor, moveTo],
  );
  const nextMonth = useCallback(
    () => moveTo(addMonths(cursor, 1), false),
    [cursor, moveTo],
  );

  return (
    <div className="nuco-calendar__periodControl">
      <Button
        className="nuco-calendar__periodControlButton"
        variant="soft"
        color="neutral"
        content={formatIsoDate(firstOfMonth(shown), locale.formats.month)}
        aria-expanded={view === CalendarView.Months}
        onClick={toggleMonths}
      />
      <Button
        className="nuco-calendar__periodControlButton"
        variant="soft"
        color="neutral"
        content={String(shown.year)}
        aria-expanded={view === CalendarView.Years}
        onClick={toggleYears}
      />

      {view === CalendarView.Days && (
        <div className="nuco-calendar__monthButtons">
          <IconButton
            variant="soft"
            color="neutral"
            onClick={previousMonth}
            disabled={firstOfMonth(shown) <= first}
            content={<ChevronLeft size="1rem" aria-hidden="true" />}
            aria-label={t("ui.calendar.previousMonth")}
          />
          <IconButton
            variant="soft"
            color="neutral"
            onClick={nextMonth}
            disabled={lastOfMonth(shown) >= last}
            content={<ChevronRight size="1rem" aria-hidden="true" />}
            aria-label={t("ui.calendar.nextMonth")}
          />
        </div>
      )}
    </div>
  );
};
